import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Logs.css';
import {
  Layout,
  Card,
  Typography,
  Row,
  Col,
  Button,
  Table,
  Tag,
  Space,
  Statistic,
  Input,
  Select,
  DatePicker,
  message,
  Empty,
  Spin,
  Alert,
  Badge,
  Tooltip,
  Tabs,
  Modal
} from 'antd';
import {
  HistoryOutlined,
  SearchOutlined,
  EyeOutlined,
  FileTextOutlined,
  FileImageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined,
  ReloadOutlined,
  FilterOutlined,
  ArrowLeftOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { applicationAPI, documentTypesAPI } from '../../services/api';
import AppHeader from '../../components/Header';
import LoadingAnimation from '../../components/LoadingAnimation';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface Application {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'suspended';
  quota_limit: number;
  quota_used: number;
  rate_limit_per_minute: number;
  total_requests: number;
  created_at: string;
  recent_requests?: {
    total_today: number;
    completed_today: number;
    failed_today: number;
    last_request_at?: string;
  };
}

interface GlobalLogRequest {
  id: string;
  application_id: string;
  application_name?: string;
  document_type: string;
  status: string;
  confidence_score: number;
  processing_time_ms: number;
  created_at: string;
  raw_text?: string;
  extracted_data?: any;
  error_message?: string;
  image_url?: string;
}

interface LogsOverview {
  total_applications: number;
  total_requests_today: number;
  total_requests_week: number;
  success_rate: number;
  top_applications: Array<{
    id: string;
    name: string;
    request_count: number;
  }>;
}

interface GlobalLogsStats {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  success_rate: number;
  avg_confidence_score: number;
  avg_processing_time: number;
  requests_by_day: Record<string, number>;
  requests_by_application: Array<{
    application_id: string;
    application_name?: string;
    request_count: number;
    successful_requests: number;
    failed_requests: number;
  }>;
  document_type_breakdown: Record<string, number>;
  status_breakdown: Record<string, number>;
}

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('global'); // Default to global logs
  const [applications, setApplications] = useState<Application[]>([]);
  const [logsOverview, setLogsOverview] = useState<LogsOverview | null>(null);
  const [globalLogs, setGlobalLogs] = useState<GlobalLogRequest[]>([]);
  const [globalLogsStats, setGlobalLogsStats] = useState<GlobalLogsStats | null>(null);
  const [globalLogsTotal, setGlobalLogsTotal] = useState(0);
  const [globalLogsPage, setGlobalLogsPage] = useState(1);
  const [globalLogsPageSize, setGlobalLogsPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [globalLogsLoading, setGlobalLogsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all');
  const [applicationFilter, setApplicationFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedGlobalLogRequest, setSelectedGlobalLogRequest] = useState<GlobalLogRequest | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  
  // Image modal state
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImageRequestId, setCurrentImageRequestId] = useState<string>('');
  
  // Dynamic filters from actual data
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const loadingRef = useRef({ globalLogs: false, globalLogsStats: false, initialData: false });

  const loadData = async () => {
    if (loadingRef.current.initialData) return;
    
    try {
      loadingRef.current.initialData = true;
      setLoading(true);
      
      // Load applications and document types in parallel (optimized)
      const [appsResponse, documentTypesResponse] = await Promise.all([
        applicationAPI.getApplications(),
        documentTypesAPI.getDocumentTypes(true)
      ]);
      
      const appsData = appsResponse.data.data || [];
      
      // Set applications directly without fetching individual logs
      // (we already have total_requests from the application data)
      const appsWithStats = appsData.map((app: Application) => ({
        ...app,
        recent_requests: {
          total_today: 0, // Can be calculated from global logs if needed
          completed_today: 0,
          failed_today: 0,
          last_request_at: null
        }
      }));
      
      setApplications(appsWithStats);
      
      // Calculate overview stats from application data
      const overview: LogsOverview = {
        total_applications: appsWithStats.length,
        total_requests_today: 0, // Will be populated from global logs stats
        total_requests_week: appsWithStats.reduce((sum: number, app: Application) => sum + app.total_requests, 0),
        success_rate: 95,
        top_applications: appsWithStats
          .sort((a: Application, b: Application) => b.total_requests - a.total_requests)
          .slice(0, 5)
          .map((app: Application) => ({
            id: app.id,
            name: app.name,
            request_count: app.total_requests
          }))
      };
      
      setLogsOverview(overview);

      // Set document types from parallel fetch
      if (documentTypesResponse.data.success && documentTypesResponse.data.data) {
        const docTypes = documentTypesResponse.data.data.map((dt: any) => dt.document_type);
        setAvailableDocumentTypes(docTypes);
      } else {
        setAvailableDocumentTypes([]);
      }
      
    } catch (error) {
      console.error('Error loading logs data:', error);
      message.error('Failed to load logs data');
    } finally {
      setLoading(false);
      loadingRef.current.initialData = false;
    }
  };

  const loadGlobalLogs = async () => {
    if (loadingRef.current.globalLogs) return;
    
    try {
      loadingRef.current.globalLogs = true;
      setGlobalLogsLoading(true);
      
      const filters: any = {};
      
      if (statusFilter !== 'all') {
        filters.status = [statusFilter];
      }
      
      if (documentTypeFilter !== 'all') {
        filters.document_type = [documentTypeFilter];
      }
      
      if (applicationFilter !== 'all') {
        filters.application_id = [applicationFilter];
      }
      
      if (dateRange && dateRange.length === 2) {
        filters.date_from = dateRange[0];
        filters.date_to = dateRange[1];
      }
      
      const params = {
        page: globalLogsPage,
        pageSize: globalLogsPageSize,
        search: searchTerm || undefined,
        ...filters
      };
      
      console.log('Loading global logs with params:', params);
      const response = await applicationAPI.getGlobalLogs(params);
      const data = response.data.data;
      
      setGlobalLogs(data.requests || []);
      setGlobalLogsTotal(data.total || 0);
      
      // Extract status options from the data
      if (data.requests && data.requests.length > 0) {
        const statuses = Array.from(new Set<string>(data.requests.map((req: GlobalLogRequest) => req.status).filter(Boolean) as string[]));
        setAvailableStatuses(statuses);
        
        // If document types are still empty, extract them from logs as backup
        if (availableDocumentTypes.length === 0) {
          const docTypesFromLogs = Array.from(new Set<string>(
            data.requests.map((req: GlobalLogRequest) => req.document_type).filter(Boolean) as string[]
          ));
          if (docTypesFromLogs.length > 0) {
            setAvailableDocumentTypes(docTypesFromLogs);
            console.log('Using document types from logs data (backup):', docTypesFromLogs);
          }
        }
      }
      
    } catch (error) {
      console.error('Error loading global logs:', error);
      message.error('Failed to load global logs');
    } finally {
      setGlobalLogsLoading(false);
      loadingRef.current.globalLogs = false;
    }
  };

  const loadGlobalLogsStats = async () => {
    if (loadingRef.current.globalLogsStats) return;
    
    try {
      loadingRef.current.globalLogsStats = true;
      const response = await applicationAPI.getGlobalLogsStats();
      setGlobalLogsStats(response.data.data);
    } catch (error) {
      console.error('Error loading global logs stats:', error);
    } finally {
      loadingRef.current.globalLogsStats = false;
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'global') {
      // Check if already loading before scheduling
      if (loadingRef.current.globalLogs || loadingRef.current.globalLogsStats) {
        return;
      }
      
      loadGlobalLogs();
      loadGlobalLogsStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, globalLogsPage, globalLogsPageSize, searchTerm, statusFilter, documentTypeFilter, applicationFilter, dateRange]);

  const handleViewLogs = (application: Application) => {
    navigate(`/api-management/applications/${application.id}?tab=logs`);
  };

  const handleGlobalLogDetails = (record: GlobalLogRequest) => {
    setSelectedGlobalLogRequest(record);
    setDetailsModalVisible(true);
  };

  const handleRegenerateGlobalImageUrl = async (request: GlobalLogRequest) => {
    try {
      const response = await applicationAPI.regenerateGlobalLogImageUrl(request.id, {
        expiration_hours: 24
      });
      
      if (response.data.success) {
        const newImageUrl = response.data.data.image_url;
        
        // Update the request in the global logs list
        setGlobalLogs(prevLogs => 
          prevLogs.map(log => 
            log.id === request.id 
              ? { ...log, image_url: newImageUrl }
              : log
          )
        );
        
        // Update selected request if it's the same one
        if (selectedGlobalLogRequest && selectedGlobalLogRequest.id === request.id) {
          setSelectedGlobalLogRequest({ ...selectedGlobalLogRequest, image_url: newImageUrl });
        }
        
        message.success('Image URL regenerated successfully');
      } else {
        message.error(response.data.message || 'Failed to regenerate image URL');
      }
    } catch (error) {
      console.error('Error regenerating global image URL:', error);
      message.error('Failed to regenerate image URL');
    }
  };

  const handleViewImage = (record: GlobalLogRequest) => {
    if (record.image_url) {
      setCurrentImageUrl(record.image_url);
      setCurrentImageRequestId(record.id);
      setImageError(false);
      setImageModalVisible(true);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleRefreshImageInModal = async () => {
    if (!currentImageRequestId) return;
    
    setImageLoading(true);
    try {
      const response = await applicationAPI.regenerateGlobalLogImageUrl(currentImageRequestId, {
        expiration_hours: 24
      });
      
      if (response.data.success) {
        const newImageUrl = response.data.data.image_url;
        setCurrentImageUrl(newImageUrl);
        setImageError(false);
        
        // Update the main logs data as well
        setGlobalLogs(prevLogs => 
          prevLogs.map(log => 
            log.id === currentImageRequestId 
              ? { ...log, image_url: newImageUrl }
              : log
          )
        );
        
        // Update selected request if it's the same one (for details modal)
        if (selectedGlobalLogRequest && selectedGlobalLogRequest.id === currentImageRequestId) {
          setSelectedGlobalLogRequest({ ...selectedGlobalLogRequest, image_url: newImageUrl });
        }
        
        message.success('Image URL refreshed successfully');
      } else {
        message.error(response.data.message || 'Failed to refresh image URL');
      }
    } catch (error) {
      console.error('Error refreshing image URL:', error);
      message.error('Failed to refresh image URL');
    } finally {
      setImageLoading(false);
    }
  };

  const handleExportGlobalLogs = async (format: 'csv' | 'json' | 'xlsx') => {
    try {
      const filters: any = {};
      
      if (statusFilter !== 'all') {
        filters.status = [statusFilter];
      }
      
      if (documentTypeFilter !== 'all') {
        filters.document_type = [documentTypeFilter];
      }
      
      if (applicationFilter !== 'all') {
        filters.application_id = [applicationFilter];
      }
      
      if (dateRange && dateRange.length === 2) {
        filters.date_from = dateRange[0];
        filters.date_to = dateRange[1];
      }
      
      const response = await applicationAPI.exportGlobalLogs({
        format,
        filters
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `global_logs_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success(`Global logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting global logs:', error);
      message.error('Failed to export global logs');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'green';
      case 'failed':
      case 'error':
        return 'red';
      case 'processing':
        return 'blue';
      default:
        return 'orange';
    }
  };

  const formatConfidenceScore = (score: number) => {
    return score ? `${(score * 100).toFixed(1)}%` : 'N/A';
  };

  const formatProcessingTime = (timeMs: number) => {
    return timeMs ? `${timeMs}ms` : 'N/A';
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const applicationColumns = [
    {
      title: 'Application',
      key: 'application',
      render: (_: any, record: Application) => (
        <div>
          <Text strong>{record.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'inactive' ? 'orange' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Total Requests',
      dataIndex: 'total_requests',
      key: 'total_requests',
      width: 120,
      render: (count: number) => (
        <Statistic
          value={count}
          valueStyle={{ fontSize: '16px' }}
        />
      ),
    },
    {
      title: 'Today',
      key: 'today_stats',
      width: 100,
      render: (_: any, record: Application) => (
        <div>
          <Text strong>{record.recent_requests?.total_today || 0}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            requests
          </Text>
        </div>
      ),
    },
    {
      title: 'Usage',
      key: 'usage',
      width: 150,
      render: (_: any, record: Application) => {
        const percentage = record.quota_limit > 0 ? (record.quota_used / record.quota_limit) * 100 : 0;
        return (
          <div>
            <Text>{record.quota_used.toLocaleString()} / {record.quota_limit.toLocaleString()}</Text>
            <br />
            <Tag color={percentage > 80 ? 'red' : percentage > 60 ? 'orange' : 'green'}>
              {percentage.toFixed(1)}%
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Application) => (
        <Space>
          <Tooltip title="View Application Logs">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleViewLogs(record)}
              style={{
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
              }}
            >
              View Logs
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const globalLogsColumns = [
    {
      title: 'Request ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (id: string) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {id.substring(0, 8)}...
        </Text>
      ),
    },
    {
      title: 'Application',
      dataIndex: 'application_name',
      key: 'application_name',
      width: 150,
      render: (name: string, record: GlobalLogRequest) => (
        <div>
          <Text strong>{name || 'Unknown'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.application_id.substring(0, 8)}...
          </Text>
        </div>
      ),
    },
    {
      title: 'Document Type',
      dataIndex: 'document_type',
      key: 'document_type',
      width: 120,
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence_score',
      key: 'confidence_score',
      width: 100,
      render: (score: number) => (
        <Text style={{ 
          color: score > 0.8 ? '#52c41a' : score > 0.6 ? '#faad14' : '#ff4d4f',
          fontWeight: 'bold'
        }}>
          {formatConfidenceScore(score)}
        </Text>
      ),
    },
    {
      title: 'Processing Time',
      dataIndex: 'processing_time_ms',
      key: 'processing_time_ms',
      width: 120,
      render: (time: number) => (
        <Text>{formatProcessingTime(time)}</Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        <div>
          <Text>{new Date(date).toLocaleDateString()}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {new Date(date).toLocaleTimeString()}
          </Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: any, record: GlobalLogRequest) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleGlobalLogDetails(record)}
            >
              Details
            </Button>
          </Tooltip>
          {record.image_url && (
            <Tooltip title="View Image">
              <Button
                size="small"
                icon={<FileImageOutlined />}
                onClick={() => handleViewImage(record)}
              />
            </Tooltip>
          )}
          {record.image_url && (
            <Tooltip title="Regenerate Image URL">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRegenerateGlobalImageUrl(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <Layout className="layout-container">
        <AppHeader />
        <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
          <LoadingAnimation
            type="pulse"
            message="Loading logs data..."
            size="large"
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="layout-container">
      <AppHeader />
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }} className="logs-page">
        {/* Back Button */}
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
            style={{ marginBottom: '8px' }}
          >
            Back to Dashboard
          </Button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <HistoryOutlined /> Request Logs
          </Title>
          <Text type="secondary">
            Monitor and analyze OCR request logs across all applications
          </Text>
        </div>

        {/* Tabs for different views */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: '24px' }}>
          {/* Global Logs Tab - only one! */}
          <TabPane 
            tab={
              <span>
                <GlobalOutlined />
                Global Logs
              </span>
            }
            key="global"
          >
            {/* Global Stats Cards */}
            {globalLogsStats && (
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Requests"
                      value={globalLogsStats.total_requests}
                      prefix={<BarChartOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Successful"
                      value={globalLogsStats.successful_requests}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Failed"
                      value={globalLogsStats.failed_requests}
                      valueStyle={{ color: '#ff4d4f' }}
                      prefix={<ExclamationCircleOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Success Rate"
                      value={globalLogsStats.success_rate}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            )}
            {/* Global Logs Actions */}
            <Card title="Global Logs Actions" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={() => {
                      loadGlobalLogs();
                      loadGlobalLogsStats();
                    }}
                    loading={globalLogsLoading}
                  >
                    Refresh Global Logs
                  </Button>
                </Col>
                <Col>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleExportGlobalLogs('csv')}
                  >
                    Export CSV
                  </Button>
                </Col>
                <Col>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleExportGlobalLogs('json')}
                  >
                    Export JSON
                  </Button>
                </Col>
              </Row>
            </Card>
            {/* Global Logs Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={8}>
                <Input
                  placeholder="Search global logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: '100%' }}
                  placeholder="Status"
                >
                  <Option value="all">All Status</Option>
                  {availableStatuses.map(status => (
                    <Option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={documentTypeFilter}
                  onChange={setDocumentTypeFilter}
                  style={{ width: '100%' }}
                  placeholder="Doc Type"
                >
                  <Option value="all">All Types</Option>
                  {availableDocumentTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={applicationFilter}
                  onChange={setApplicationFilter}
                  style={{ width: '100%' }}
                  placeholder="Application"
                >
                  <Option value="all">All Apps</Option>
                  {applications.map(app => (
                    <Option key={app.id} value={app.id}>{app.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={["Start Date", "End Date"]}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange([dates[0].toISOString(), dates[1].toISOString()]);
                    } else {
                      setDateRange(null);
                    }
                  }}
                />
              </Col>
            </Row>
            {/* Global Logs Table */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <GlobalOutlined />
                  <span>Global Request Logs</span>
                  <Badge count={globalLogsTotal} style={{ marginLeft: '8px' }} />
                </div>
              }
              extra={
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadGlobalLogs}
                  loading={globalLogsLoading}
                >
                  Refresh
                </Button>
              }
            >
              {globalLogsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                </div>
              ) : globalLogs.length === 0 ? (
                <Empty 
                  description="No global logs found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '40px 0' }}
                />
              ) : (
                <Table
                  columns={globalLogsColumns}
                  dataSource={globalLogs}
                  rowKey="id"
                  pagination={{
                    current: globalLogsPage,
                    pageSize: globalLogsPageSize,
                    total: globalLogsTotal,
                    onChange: (page, pageSize) => {
                      setGlobalLogsPage(page);
                      setGlobalLogsPageSize(pageSize || 10);
                    },
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
                  }}
                  scroll={{ x: 1200 }}
                />
              )}
            </Card>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <AppstoreOutlined />
                Applications Overview
              </span>
            } 
            key="applications"
          >
            {/* Overview Cards */}
            {logsOverview && (
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Applications"
                      value={logsOverview.total_applications}
                      prefix={<FileTextOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Requests Today"
                      value={logsOverview.total_requests_today}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Requests"
                      value={logsOverview.total_requests_week}
                      prefix={<BarChartOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Success Rate"
                      value={logsOverview.success_rate}
                      precision={1}
                      suffix="%"
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Quick Actions Card */}
            <Card title="Quick Actions" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={loadData}>
                    Refresh Data
                  </Button>
                </Col>

                <Col>
                  <Button icon={<FileTextOutlined />} onClick={() => navigate('/api-management')}>
                    Manage Applications
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Search and Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} md={12}>
                <Input
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                  size="large"
                  allowClear
                  style={{ 
                    width: '100%'
                  }}
                />
              </Col>
              <Col xs={24} md={12}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12}>
                    <Select
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ width: '100%' }}
                      size="large"
                      placeholder="Filter by status"
                    >
                      <Option value="all">All Status</Option>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                      <Option value="suspended">Suspended</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ textAlign: 'right' }}>
                      <Badge 
                        count={filteredApplications.length} 
                        style={{ backgroundColor: '#52c41a' }}
                      >
                        <Text strong style={{ marginRight: '8px' }}>
                          Found
                        </Text>
                      </Badge>
                      <Text type="secondary" style={{ marginLeft: '8px' }}>
                        / {applications.length} total
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Applications Table */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileTextOutlined />
                  <span>Applications</span>
                  <Badge count={filteredApplications.length} style={{ marginLeft: '8px' }} />
                </div>
              }
              extra={
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadData}
                  loading={loading}
                >
                  Refresh
                </Button>
              }
            >
              {filteredApplications.length === 0 ? (
                <Empty 
                  description="No applications found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '40px 0' }}
                />
              ) : (
                <Table
                  columns={applicationColumns}
                  dataSource={filteredApplications}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} applications`,
                  }}
                  scroll={{ x: 800 }}
                />
              )}
            </Card>
          </TabPane>
        </Tabs>

        {/* Request Details Modal */}
        <Modal
          title="Request Details"
          visible={detailsModalVisible}
          onCancel={() => setDetailsModalVisible(false)}
          footer={[
            selectedGlobalLogRequest?.image_url && (
              <Button 
                key="regenerate"
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (selectedGlobalLogRequest) {
                    handleRegenerateGlobalImageUrl(selectedGlobalLogRequest);
                  }
                }}
              >
                Regenerate Image URL
              </Button>
            ),
            <Button key="close" onClick={() => setDetailsModalVisible(false)}>
              Close
            </Button>
          ]}
          width={800}
        >
          {selectedGlobalLogRequest && (
            <div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card title="Basic Information" size="small">
                    <p><strong>Request ID:</strong> {selectedGlobalLogRequest.id}</p>
                    <p><strong>Application:</strong> {selectedGlobalLogRequest.application_name}</p>
                    <p><strong>Document Type:</strong> {selectedGlobalLogRequest.document_type}</p>
                    <p><strong>Status:</strong> <Tag color={getStatusColor(selectedGlobalLogRequest.status)}>{selectedGlobalLogRequest.status}</Tag></p>
                    <p><strong>Created:</strong> {new Date(selectedGlobalLogRequest.created_at).toLocaleString()}</p>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Processing Information" size="small">
                    <p><strong>Confidence Score:</strong> {formatConfidenceScore(selectedGlobalLogRequest.confidence_score)}</p>
                    <p><strong>Processing Time:</strong> {formatProcessingTime(selectedGlobalLogRequest.processing_time_ms)}</p>
                    {selectedGlobalLogRequest.error_message && (
                      <p><strong>Error:</strong> <Text type="danger">{selectedGlobalLogRequest.error_message}</Text></p>
                    )}
                  </Card>
                </Col>
              </Row>
              {selectedGlobalLogRequest.raw_text && (
                <Card title="Raw Text" size="small" style={{ marginTop: '16px' }}>
                  <Text code style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedGlobalLogRequest.raw_text}
                  </Text>
                </Card>
              )}
              {selectedGlobalLogRequest.extracted_data && Object.keys(selectedGlobalLogRequest.extracted_data).length > 0 && (
                <Card title="Extracted Data" size="small" style={{ marginTop: '16px' }}>
                  <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(selectedGlobalLogRequest.extracted_data, null, 2)}
                  </pre>
                </Card>
              )}
              <Button 
                type="primary" 
                icon={<ReloadOutlined />} 
                onClick={() => handleRegenerateGlobalImageUrl(selectedGlobalLogRequest)}
                style={{ marginTop: '16px' }}
              >
                Regenerate Image URL
              </Button>
            </div>
          )}
        </Modal>

        {/* Image View Modal */}
        <Modal
          title="View Image"
          open={imageModalVisible}
          onCancel={() => {
            setImageModalVisible(false);
            setCurrentImageUrl('');
            setImageError(false);
            setCurrentImageRequestId('');
          }}
          footer={[
            <Button key="close" onClick={() => {
              setImageModalVisible(false);
              setCurrentImageUrl('');
              setImageError(false);
              setCurrentImageRequestId('');
            }}>
              Close
            </Button>,
            imageError && (
              <Button 
                key="refresh" 
                type="primary" 
                icon={<ReloadOutlined />}
                loading={imageLoading}
                onClick={handleRefreshImageInModal}
              >
                Refresh Image
              </Button>
            )
          ].filter(Boolean)}
          width={800}
          centered
        >
          <div style={{ textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imageError ? (
              <div>
                <Alert
                  message="Image failed to load"
                  description="The image URL may have expired. Click 'Refresh Image' to get a new URL."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  loading={imageLoading}
                  onClick={handleRefreshImageInModal}
                >
                  Refresh Image
                </Button>
              </div>
            ) : (
              <img
                src={currentImageUrl}
                alt="Document"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '500px', 
                  objectFit: 'contain'
                }}
                onError={handleImageError}
              />
            )}
          </div>
        </Modal>
      </Content>
    </Layout>
  );
};

export default LogsPage;
